import $i18n, { withI18n } from 'panda-i18n';
import locale from '@/locale';
import React, { forwardRef, memo, useEffect, useImperativeHandle, useState, } from 'react';
import classNames from 'classnames';
import { CnPopup } from '@/components/cn-dialog';
import { mergeProps } from './with-default-props';
import { withNativeProps } from '@/utils/with-native-props';
import { usePropsValue } from '../../utils/use-props-value';
import { CnPickerView } from '@/components/cn-picker-view';
import { generateColumnsExtend, useColumnsExtend, } from '@/components/cn-picker-view/columns-extend';
import { useMemoizedFn } from 'ahooks';
import { CnSafeArea } from '@/components/cn-safe-area';
import { defaultRenderLabel } from './picker-utils';
const classPrefix = 'cn-ui-m-picker';
const defaultProps = {
    defaultValue: [],
    closeOnMaskClick: true,
    renderLabel: defaultRenderLabel,
    destroyOnClose: false,
    forceRender: false,
};
const Picker = memo(forwardRef((p, ref) => {
    const props = mergeProps(defaultProps, {
        confirmText: $i18n.get({ id: 'Determine', dm: '确定', ns: 'CnPicker' }),
        cancelText: $i18n.get({ id: 'Cancel', dm: '取消', ns: 'CnPicker' }),
    }, p);
    const [visible, setVisible] = usePropsValue({
        value: props.visible,
        defaultValue: false,
        onChange: (v) => {
            if (v === false) {
                props.onClose?.();
            }
        },
    });
    const actions = {
        toggle: () => {
            setVisible((v) => !v);
        },
        open: () => {
            setVisible(true);
        },
        close: () => {
            setVisible(false);
        },
    };
    useImperativeHandle(ref, () => actions);
    const [value, setValue] = usePropsValue({
        ...props,
        onChange: (val) => {
            const extend = generateColumnsExtend(props.columns, val);
            props.onConfirm?.(val, extend);
        },
    });
    const extend = useColumnsExtend(props.columns, value);
    const [innerValue, setInnerValue] = useState(value);
    useEffect(() => {
        if (innerValue !== value) {
            setInnerValue(value);
        }
    }, [visible]);
    useEffect(() => {
        if (!visible) {
            setInnerValue(value);
        }
    }, [value]);
    const onChange = useMemoizedFn((val, ext) => {
        setInnerValue(val);
        if (visible) {
            props.onSelect?.(val, ext);
        }
    });
    const pickerElement = withNativeProps(props, React.createElement("div", { className: classPrefix },
        React.createElement("div", { className: `${classPrefix}-header` },
            React.createElement("a", { role: "button", className: `${classPrefix}-header-button`, onClick: () => {
                    props.onCancel?.();
                    setVisible(false);
                } }, props.cancelText),
            React.createElement("div", { className: `${classPrefix}-header-title` }, props.title),
            React.createElement("a", { role: "button", className: classNames(`${classPrefix}-header-button`, props.loading && `${classPrefix}-header-button-disabled`), onClick: () => {
                    if (props.loading)
                        return;
                    setValue(innerValue, true);
                    setVisible(false);
                }, "aria-disabled": props.loading }, props.confirmText)),
        React.createElement("div", { className: `${classPrefix}-body` },
            React.createElement(CnPickerView, { loading: props.loading, loadingContent: props.loadingContent, columns: props.columns, renderLabel: props.renderLabel, value: innerValue, mouseWheel: props.mouseWheel, onChange: onChange }))));
    const popupElement = (React.createElement(CnPopup, { style: props.popupStyle, className: classNames(`${classPrefix}-popup`, props.popupClassName), visible: visible, closeMode: ['mask'], placement: "bottom", onMaskClick: () => {
            if (!props.closeOnMaskClick)
                return;
            props.onCancel?.();
            setVisible(false);
        }, getContainer: props.getContainer, destroyOnClose: props.destroyOnClose, afterShow: props.afterShow, afterClose: props.afterClose, onClick: props.onClick, forceRender: props.forceRender, stopPropagation: props.stopPropagation },
        pickerElement,
        React.createElement(CnSafeArea, { position: "bottom" })));
    return (React.createElement(React.Fragment, null,
        popupElement,
        props.children?.(extend.items, actions)));
}));
Picker.displayName = 'CnPicker';
const CnPicker = withI18n(Picker, { locale, _noHoc: true });
export { CnPicker };
